import { useState, useEffect } from 'react';
import { SkillsAssessmentService } from '@/services/skillsAssessment';
import { useToast } from './useToast';
import type { Assessment } from '@/types/database';

// Local interfaces for the hook
interface Question {
  id: string;
  type: string;
  question: string;
  options?: string[];
  correct?: string | string[];
  points: number;
  explanation?: string;
  timeLimit?: number;
  template?: string;
  expectedOutput?: string;
}

interface AssessmentResult {
  id: string;
  assessmentId: string;
  candidateName: string;
  candidateEmail: string;
  score: number;
  maxScore: number;
  percentage: number;
  completedAt: string;
  timeSpent: number;
  answers: Array<{
    questionId: string;
    answer: any;
    correct: boolean;
    points: number;
    maxPoints: number;
  }>;
  skillsBreakdown: Array<{
    skill: string;
    score: number;
    maxScore: number;
    percentage: number;
  }>;
  status: 'completed' | 'in_progress' | 'not_started';
  notes?: string;
}

export function useSkillsAssessments() {
  const { addToast } = useToast();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssessments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [assessmentsData, resultsData] = await Promise.all([
        SkillsAssessmentService.getAssessments(),
        SkillsAssessmentService.getAssessmentResults()
      ]);
      
      setAssessments(assessmentsData);
      setResults(resultsData);
    } catch (err) {
      console.error('Error fetching skills assessments:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch assessments');
      setAssessments([]);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Save assessment to database
  const saveAssessment = async (assessmentData: {
    name: string;
    type: string;
    difficulty: string;
    duration: number;
    skills: string[];
    questions: Question[];
  }): Promise<Assessment | null> => {
    try {
      const savedAssessment = await SkillsAssessmentService.saveAssessment(assessmentData);
      if (savedAssessment) {
        setAssessments(prev => [savedAssessment, ...prev]);
        addToast('Assessment saved successfully!', 'success');
        return savedAssessment;
      }
      return null;
    } catch (error) {
      console.error('Error saving assessment:', error);
      addToast('Failed to save assessment', 'error');
      return null;
    }
  };

  // Delete assessment
  const deleteAssessment = async (assessmentId: string): Promise<boolean> => {
    try {
      const success = await SkillsAssessmentService.deleteAssessment(assessmentId);
      if (success) {
        setAssessments(prev => prev.filter(a => a.id !== assessmentId));
        addToast('Assessment deleted successfully', 'success');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting assessment:', error);
      addToast('Failed to delete assessment', 'error');
      return false;
    }
  };

  // Save assessment result
  const saveAssessmentResult = async (resultData: {
    assessmentId: string;
    candidateName: string;
    candidateEmail: string;
    score: number;
    maxScore: number;
    percentage: number;
    timeSpent: number;
    notes?: string;
  }): Promise<AssessmentResult | null> => {
    try {
      const savedResult = await SkillsAssessmentService.saveAssessmentResult(resultData);
      if (savedResult) {
        setResults(prev => [savedResult, ...prev]);
        // Update assessment usage count
        setAssessments(prev => prev.map(a => 
          a.id === resultData.assessmentId 
            ? { ...a, timesUsed: a.timesUsed + 1, lastUsed: new Date().toISOString() }
            : a
        ));
        addToast('Assessment result saved successfully!', 'success');
        return savedResult;
      }
      return null;
    } catch (error) {
      console.error('Error saving assessment result:', error);
      addToast('Failed to save assessment result', 'error');
      return null;
    }
  };

  // Update assessment result
  const updateAssessmentResult = async (
    resultId: string,
    updates: Partial<{
      candidateName: string;
      candidateEmail: string;
      score: number;
      maxScore: number;
      percentage: number;
      timeSpent: number;
      notes?: string;
    }>
  ): Promise<boolean> => {
    try {
      const success = await SkillsAssessmentService.updateAssessmentResult(resultId, updates);
      if (success) {
        // Refresh results data
        const resultsData = await SkillsAssessmentService.getAssessmentResults();
        setResults(resultsData);
        addToast('Assessment result updated successfully!', 'success');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating assessment result:', error);
      addToast('Failed to update assessment result', 'error');
      return false;
    }
  };

  // Delete assessment result
  const deleteAssessmentResult = async (resultId: string): Promise<boolean> => {
    try {
      const success = await SkillsAssessmentService.deleteAssessmentResult(resultId);
      if (success) {
        setResults(prev => prev.filter(r => r.id !== resultId));
        addToast('Assessment result deleted successfully', 'success');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting assessment result:', error);
      addToast('Failed to delete assessment result', 'error');
      return false;
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  // Listen for dashboard refresh events
  useEffect(() => {
    const handleRefresh = () => {
      fetchAssessments();
    };

    window.addEventListener('dashboardRefresh', handleRefresh);
    return () => window.removeEventListener('dashboardRefresh', handleRefresh);
  }, []);

  return {
    assessments,
    results,
    isLoading,
    error,
    saveAssessment,
    deleteAssessment,
    saveAssessmentResult,
    updateAssessmentResult,
    deleteAssessmentResult,
    refetch: fetchAssessments
  };
} 